// Sorterbar tabell för screener. Klick på header cyklar asc -> desc -> none.
// Hela raden är klickbar och navigerar till /stocks/:ticker.

import { useNavigate } from 'react-router-dom';
import { fmtMarketCap, fmtNumber, rsiLabel, rsiStyle } from '../../lib/format';
import {
  type ScreenerState,
  type SortField,
  type SortDir,
} from '../../lib/screener';
import type { Stock } from '../../types/stock';

interface Props {
  stocks: Stock[];
  loading: boolean;
  sort: ScreenerState['sort'];
  onSortChange: (next: ScreenerState['sort']) => void;
}

interface ColumnDef {
  field: SortField;
  label: string;
  align?: 'left' | 'right';
  title?: string;
}

const COLUMNS: ColumnDef[] = [
  { field: 'ticker', label: 'Ticker' },
  { field: 'name', label: 'Namn' },
  { field: 'price', label: 'Pris', align: 'right' },
  { field: 'pe_ratio', label: 'P/E', align: 'right' },
  { field: 'dividend_yield', label: 'Div.', align: 'right' },
  { field: 'market_cap', label: 'Börsvärde', align: 'right' },
  { field: 'rsi_14', label: 'RSI(14)', align: 'right', title: 'Relative Strength Index, 14 dagar' },
];

function nextDir(current: SortDir, clicked: SortField, currentField: SortField): SortDir {
  if (currentField !== clicked) return 'asc';
  if (current === 'asc') return 'desc';
  if (current === 'desc') return null;
  return 'asc';
}

export default function ScreenerTable({ stocks, loading, sort, onSortChange }: Props) {
  const navigate = useNavigate();

  const onHeaderClick = (field: SortField) => {
    const dir = nextDir(sort.dir, field, sort.field);
    onSortChange({ field, dir });
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <tr>
            {COLUMNS.map((c) => {
              const active = sort.field === c.field && sort.dir;
              const arrow = active === 'asc' ? '▲' : active === 'desc' ? '▼' : '';
              return (
                <th
                  key={c.field}
                  title={c.title}
                  onClick={() => onHeaderClick(c.field)}
                  className={`cursor-pointer select-none px-3 py-3 ${c.align === 'right' ? 'text-right' : ''} hover:bg-slate-100 dark:hover:bg-slate-700`}
                >
                  {c.label}
                  {arrow && <span className="ml-1 text-indigo-600 dark:text-indigo-400">{arrow}</span>}
                </th>
              );
            })}
            <th className="px-3 py-3">Sektor</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
          {loading && (
            <tr>
              <td colSpan={COLUMNS.length + 1} className="px-3 py-8 text-center text-slate-500 dark:text-slate-400">
                Laddar…
              </td>
            </tr>
          )}
          {!loading && stocks.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length + 1} className="px-3 py-8 text-center text-slate-500 dark:text-slate-400">
                Inga aktier matchar filtret.
              </td>
            </tr>
          )}
          {!loading &&
            stocks.map((s) => (
              <tr
                key={s.id}
                onClick={() => navigate(`/stocks/${encodeURIComponent(s.ticker)}`)}
                className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
              >
                <td className="px-3 py-3 font-mono font-medium text-slate-900 dark:text-white">
                  {s.ticker}
                </td>
                <td className="px-3 py-3 text-slate-700 dark:text-slate-300">
                  {s.name || '—'}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-slate-900 dark:text-white">
                  {fmtNumber(s.price, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                  {s.currency && <span className="text-xs text-slate-500 dark:text-slate-400">{s.currency}</span>}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-slate-700 dark:text-slate-300">
                  {fmtNumber(s.pe_ratio, { maximumFractionDigits: 1 })}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-slate-700 dark:text-slate-300">
                  {s.dividend_yield !== undefined && s.dividend_yield !== 0
                    ? `${s.dividend_yield.toFixed(2)} %`
                    : '—'}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-slate-700 dark:text-slate-300">
                  {fmtMarketCap(s.market_cap)}
                </td>
                <td className={`px-3 py-3 text-right tabular-nums ${rsiStyle(s.rsi_14)}`}>
                  {rsiLabel(s.rsi_14)}
                </td>
                <td className="px-3 py-3 text-slate-700 dark:text-slate-300">
                  {s.sector || '—'}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
